import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, CheckCircle2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Shared";
import { API, PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/data/content";
import { useSEO } from "@/hooks/useSEO";

const inputCls = "w-full bg-bone border border-obsidian/20 px-4 py-3 text-sm text-obsidian placeholder:text-stonemuted focus:border-teak transition-colors";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-stonemuted mb-2";

export default function Contact() {
  useSEO(
    "Contact MTC Home Renovation | (519) 760-0444 · Greater Toronto Area",
    "Contact MTC Home Renovation for renovations across the GTA. Call (519) 760-0444 or email info@mtcrenovation.ca. Mon–Sat 7:30 AM–6:30 PM."
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please call us at " + PHONE_DISPLAY);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 sm:py-24" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Contact Us</Eyebrow>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-obsidian">
            Let's talk about your <em className="italic text-teak">home.</em>
          </h1>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-px">
            {[
              { icon: Phone, label: "Call or Text", value: PHONE_DISPLAY, href: PHONE_TEL, testId: "contact-phone-card" },
              { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, testId: "contact-email-card" },
            ].map((c) => (
              <Reveal key={c.label}>
                <a href={c.href} data-testid={c.testId} className="group flex items-center gap-4 border border-obsidian/15 bg-white p-6 hover:border-teak transition-colors duration-300">
                  <span className="w-12 h-12 bg-obsidian text-bone flex items-center justify-center group-hover:bg-teak transition-colors duration-300">
                    <c.icon size={19} />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-stonemuted">{c.label}</span>
                    <span className="block mt-1 font-display text-xl font-medium text-teak">{c.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
            <Reveal className="border border-obsidian/15 bg-white p-6">
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 bg-obsidian text-bone flex items-center justify-center shrink-0"><Clock size={19} /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-stonemuted">Hours</p>
                  <p className="mt-1 font-display text-xl font-medium text-obsidian">Mon – Sat</p>
                  <p className="text-sm text-stone-600">7:30 AM – 6:30 PM</p>
                </div>
              </div>
            </Reveal>
            <Reveal className="border border-obsidian/15 bg-white p-6">
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 bg-obsidian text-bone flex items-center justify-center shrink-0"><MapPin size={19} /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-stonemuted">Service Area</p>
                  <p className="mt-1 font-display text-xl font-medium text-obsidian">GTA & Kitchener-Waterloo</p>
                  <p className="text-sm text-stone-600 leading-relaxed">Toronto, Mississauga, Brampton, Vaughan, Markham, Oakville, Burlington, Kitchener, Waterloo, Cambridge, Guelph and beyond.</p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <div className="border border-obsidian/15 bg-white p-10 sm:p-14 text-center h-full flex flex-col items-center justify-center" data-testid="contact-success">
                  <CheckCircle2 size={48} className="text-teak" />
                  <h2 className="mt-6 font-display text-3xl font-medium text-obsidian">Message sent.</h2>
                  <p className="mt-4 text-stone-600 max-w-md">Thanks, {form.name.split(" ")[0]}. We'll reply to <strong>{form.email}</strong> shortly — usually the same business day.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="border border-obsidian/15 bg-white p-7 sm:p-10" data-testid="contact-form">
                  <p className="font-mono text-xs uppercase tracking-[0.22em] text-teak">Quick Inquiry</p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="con-name">Name *</label>
                      <input id="con-name" data-testid="contact-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Smith" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="con-email">Email *</label>
                      <input id="con-email" data-testid="contact-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@email.com" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls} htmlFor="con-phone">Phone</label>
                      <input id="con-phone" data-testid="contact-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(416) 555-0123" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls} htmlFor="con-msg">Message *</label>
                      <textarea id="con-msg" data-testid="contact-message-input" required rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="How can we help?" />
                    </div>
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={loading}
                    className="mt-8 w-full bg-obsidian text-bone py-4 text-sm font-semibold tracking-wide hover:bg-charcoal transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
